import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Link } from "react-router-dom";
import { Leaf, Building2, Scissors, Check, ArrowRight } from "lucide-react";
import { useSEO } from "../hooks/useSEO";
import WhatsAppCTA from "../components/WhatsAppCTA";

const FadeInSection = ({ children, delay = 0 }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.8, delay }}
    >
      {children}
    </motion.div>
  );
};

const services = [
  {
    id: "diseno",
    icon: Leaf,
    title: "Diseño Naturalista Integral",
    subtitle: "Para el Propietario Consciente",
    description: "Estudio técnico completo con plan maestro a largo plazo. Diseño evolutivo que integra tu propiedad con el ecosistema de Los Cabos.",
    image: "/assets/projects/chi-chi-mar/02_dsc02723.jpg",
    features: [
      "Estudio técnico del microclima",
      "Análisis de suelo y drenaje",
      "Evaluación solar y de vientos",
      "Plan maestro evolutivo",
      "Selección vegetal estratégica",
      "Diseño de sistema de riego",
      "Implementación por fases",
      "Seguimiento y ajuste continuo"
    ],
    message: "Diseñamos paisajes adaptados al clima real de tu propiedad, pensados para evolucionar contigo."
  },
  {
    id: "proyectos",
    icon: Building2,
    title: "Proyectos Estratégicos",
    subtitle: "Para el Desarrollador Estratégico",
    description: "Intervenciones de alto impacto con cronograma claro y ejecución eficiente. Maximiza el valor de tu desarrollo inmobiliario.",
    image: "/assets/projects/clements/05_despues.jpg",
    features: [
      "Plan de ejecución claro",
      "Cronograma definido",
      "Presupuesto estructurado",
      "Selección vegetal resistente",
      "Logística eficiente",
      "Entrega puntual",
      "Impacto visual inmediato",
      "Valorización del proyecto"
    ],
    message: "Paisaje ejecutado con precisión, adaptado al clima y listo para maximizar el valor del proyecto."
  },
  {
    id: "mantenimiento",
    icon: Scissors,
    title: "Cuidados del Jardín",
    subtitle: "Para quien valora su inversión",
    description: "Mantenimiento profesional con visión a largo plazo. Tu jardín siempre en su mejor momento, sin preocupaciones.",
    image: "/assets/projects/jardin-nacho/01_dsc00535.jpg",
    features: [
      "Visitas programadas",
      "Poda y limpieza",
      "Cuidado de plantas",
      "Sistema de riego",
      "Fertilización",
      "Reportes con fotos",
      "Control de plagas",
      "Atención prioritaria"
    ],
    message: "Tu jardín siempre impecable, tú sin preocupaciones."
  }
];

const process = [
  { number: "01", title: "Diagnóstico", description: "Estudio técnico del sitio y análisis ambiental" },
  { number: "02", title: "Diseño", description: "Plan maestro naturalista estructurado" },
  { number: "03", title: "Ejecución", description: "Implementación por fases con precisión" },
  { number: "04", title: "Evolución", description: "Seguimiento y ajuste continuo" }
];

export default function Servicios() {
  useSEO({ title: "Servicios", description: "Diseño naturalista integral, proyectos estratégicos y cuidado ecosistémico de jardines en Los Cabos, BCS." });
  return (
    <div className="min-h-screen pt-32 pb-24 bg-[#F9F6F0]" data-testid="servicios-page">
      {/* Header */}
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 mb-24">
        <motion.span
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-xs tracking-[0.3em] uppercase text-circulo-sand mb-4 block"
        >
          Nuestros Servicios
        </motion.span>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-5xl md:text-6xl lg:text-7xl font-serif font-medium mb-6"
        >
          Tres líneas de<br />servicio técnico
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-lg text-muted-foreground max-w-2xl"
        >
          Cada línea responde a necesidades específicas: desde el propietario que busca 
          integración a largo plazo, hasta el desarrollador que prioriza ejecución eficiente.
        </motion.p>
      </div>

      {/* Services */}
      {services.map((service, index) => (
        <section 
          key={service.id}
          id={service.id}
          className={`py-24 ${index % 2 === 0 ? "bg-[#F9F6F0]" : "bg-[#EFEAD8]"}`}
          data-testid={`service-section-${service.id}`}
        >
          <div className="max-w-[1600px] mx-auto px-6 md:px-12">
            <FadeInSection>
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${
                index % 2 === 1 ? "lg:flex-row-reverse" : ""
              }`}>
                <div className={index % 2 === 1 ? "lg:order-2" : ""}>
                  <div className="flex items-center gap-4 mb-6">
                    <service.icon className="w-10 h-10 text-circulo-green" />
                    <span className="text-xs tracking-[0.2em] uppercase text-muted-foreground">
                      {service.subtitle}
                    </span>
                  </div>
                  <h2 className="text-4xl md:text-5xl font-serif font-medium mb-6">
                    {service.title}
                  </h2>
                  <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                    {service.description}
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                    {service.features.map((feature) => (
                      <div key={feature} className="flex items-center gap-3">
                        <Check className="w-4 h-4 text-circulo-green flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="p-6 border-l-2 border-circulo-sand bg-circulo-subtle/50 mb-8">
                    <p className="text-sm italic text-muted-foreground">
                      "{service.message}"
                    </p>
                  </div>

                  <Link 
                    to="/contacto" 
                    className="btn-primary inline-flex items-center gap-2"
                  >
                    Solicitar información
                    <ArrowRight size={16} />
                  </Link>
                </div>

                <div className={index % 2 === 1 ? "lg:order-1" : ""}>
                  <div className="aspect-[4/5] overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </FadeInSection>
          </div>
        </section>
      ))}

      {/* Process Section */}
      <section className="py-24 bg-[#1C2E24]" data-testid="process-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <FadeInSection>
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-serif font-medium text-white mb-4">
                Proceso Círculo
              </h2>
              <p className="text-lg text-white/80 max-w-2xl mx-auto">
                Nuestro proceso justifica el estándar premium. Cada etapa está diseñada 
                para garantizar resultados duraderos.
              </p>
            </div>
          </FadeInSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <FadeInSection key={step.number} delay={index * 0.1}>
                <div className="text-center">
                  <span className="text-6xl font-serif text-white/20 block mb-4">
                    {step.number}
                  </span>
                  <h3 className="text-xl font-serif text-white mb-2">{step.title}</h3>
                  <p className="text-sm text-white/70">{step.description}</p>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#F9F6F0]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <FadeInSection>
            <WhatsAppCTA
              source="servicios_page"
              title="¿Cuál es tu necesidad?"
              subtitle="Comienza con un estudio de suelo. El costo se descuenta si avanzas a proyecto o mantenimiento."
              buttonText="Agendar por WhatsApp"
              message="Hola, me interesa agendar un estudio de suelo para mi propiedad."
              variant="light"
            />
          </FadeInSection>
        </div>
      </section>
    </div>
  );
}
